import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL || "https://backend.mystore.education/api";

// دالة مساعدة للحصول على التوكن
const getAuthToken = () => {
  try {
    const storedUser = JSON.parse(localStorage.getItem("loggedUser"));
    
    let token = storedUser?.token;
    if (!token) {
      token = storedUser?.data?.token;
    }
    if (!token) {
      token = storedUser?.user?.token;
    }
    if (!token) {
      token = storedUser?.result?.token;
    }

    return token;
  } catch (error) {
    console.error("❌ Error getting token:", error);
    return null;
  }
};

// Fetch contacts
export const fetchContacts = createAsyncThunk(
  "contacts/fetchContacts",
  async (arg = {}, thunkAPI) => {
    try {
      const token = getAuthToken();

      if (!token) {
        throw new Error('No authentication token found');
      }

      // دعم الاستدعاء القديم (رقم الصفحة فقط) والجديد ({ page, status })
      const { page = 1, status = "" } =
        typeof arg === "object" && arg !== null ? arg : { page: arg };

      let url = `${BASE_URL}/dashboard/contacts?page=${page}`;
      if (status) url += `&status=${status}`;

      const response = await axios.get(
        url,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": "ar",
          },
        }
      );

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Error fetching contacts"
      );
    }
  }
);

// Fetch full conversation history for a single customer (grouped by phone).
// لا يوجد endpoint للفلترة بالهاتف، لذلك نجمع كل الصفحات ونفلتر في العميل.
export const fetchContactHistory = createAsyncThunk(
  "contacts/fetchContactHistory",
  async (phone, thunkAPI) => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const headers = {
        Authorization: `Bearer ${token}`,
        "Accept-Language": "ar",
      };

      // الصفحة الأولى لمعرفة عدد الصفحات
      const first = await axios.get(`${BASE_URL}/dashboard/contacts?page=1`, { headers });
      const lastPage = first.data?.meta?.last_page || 1;
      let all = first.data?.data || [];

      // حد أقصى للأمان حتى لا نطلب عدداً ضخماً من الصفحات
      const MAX_PAGES = 50;
      const pagesToFetch = Math.min(lastPage, MAX_PAGES);

      if (pagesToFetch > 1) {
        const requests = [];
        for (let p = 2; p <= pagesToFetch; p++) {
          requests.push(axios.get(`${BASE_URL}/dashboard/contacts?page=${p}`, { headers }));
        }
        const results = await Promise.all(requests);
        results.forEach((r) => {
          all = all.concat(r.data?.data || []);
        });
      }

      const history = all
        .filter((c) => c.phone === phone)
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

      return { phone, history, truncated: lastPage > pagesToFetch };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "تعذّر تحميل سجل المحادثة"
      );
    }
  }
);

// Delete contact
export const deleteContact = createAsyncThunk(
  "contacts/deleteContact",
  async (id, thunkAPI) => {
    try {
      const token = getAuthToken();

      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.delete(
        `${BASE_URL}/dashboard/contacts/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": "ar",
          },
        }
      );

      return {
        id: id,
        message: response.data?.message || "تم حذف الرسالة بنجاح",
        success: true
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to delete contact"
      );
    }
  }
);

// Reply to contact
export const replyContact = createAsyncThunk(
  "contacts/replyContact",
  async ({ id, admin_reply, notify = true }, thunkAPI) => {
    try {
      const token = getAuthToken();

      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.post(
        `${BASE_URL}/dashboard/contacts/${id}/reply`,
        { admin_reply, notify },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": "ar",
          },
        }
      );

      return {
        id: id,
        contact: response.data?.data?.contact || null,
        message: response.data?.message || "تم الرد على الرسالة بنجاح",
        success: true
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to reply to contact"
      );
    }
  }
);

const contactSlice = createSlice({
  name: "contacts",
  initialState: {
    data: [],
    meta: null,
    loading: false,
    error: null,
    deleteLoading: false,
    replyLoading: false,
    success: false,
    // سجل محادثة عميل واحد
    history: [],
    historyPhone: null,
    historyLoading: false,
    historyError: null,
    historyTruncated: false,
  },
  reducers: {
    clearContactState: (state) => {
      state.error = null;
      state.success = false;
    },
    clearContactHistory: (state) => {
      state.history = [];
      state.historyPhone = null;
      state.historyError = null;
      state.historyTruncated = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContacts.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data || [];
        state.meta = action.payload.meta || null;
      })
      .addCase(fetchContacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchContactHistory.pending, (state) => {
        state.historyLoading = true;
        state.historyError = null;
      })
      .addCase(fetchContactHistory.fulfilled, (state, action) => {
        state.historyLoading = false;
        state.history = action.payload.history;
        state.historyPhone = action.payload.phone;
        state.historyTruncated = action.payload.truncated;
      })
      .addCase(fetchContactHistory.rejected, (state, action) => {
        state.historyLoading = false;
        state.historyError = action.payload;
      })
      .addCase(deleteContact.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteContact.fulfilled, (state, action) => {
        // console.log("Delete contact fulfilled:", action.payload);
        state.deleteLoading = false;
        state.success = true;
        state.data = state.data.filter((item) => item.id !== action.payload.id);
        // console.log("Success set to true");
      })
      .addCase(deleteContact.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      })
      .addCase(replyContact.pending, (state) => {
        state.replyLoading = true;
        state.error = null;
      })
      .addCase(replyContact.fulfilled, (state, action) => {
        state.replyLoading = false;
        state.success = true;
        if (action.payload.contact) {
          const index = state.data.findIndex((item) => item.id === action.payload.id);
          if (index !== -1) {
            state.data[index] = action.payload.contact;
          }
        }
      })
      .addCase(replyContact.rejected, (state, action) => {
        state.replyLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearContactState, clearContactHistory } = contactSlice.actions;
export default contactSlice.reducer; 